import React, { FC, useEffect, useRef, useState } from 'react'
import { Platform, ScrollView, View, Image, TouchableOpacity, Text, findNodeHandle } from 'react-native'
import { AppBackground, ResponsiveLogo } from '@ui/components'
import { ReactComponent as DigitalPetPassIcon } from '@/assets/icons/digital_petpass_svg.svg'
import { ReactComponent as AddIcon } from '@/assets/icons/add_plus_icon.svg'
import { ReactComponent as LostPinIcon } from '@/assets/icons/aena/aena_lost_pin.svg'
import { ReactComponent as AenaDocumentacionIcon } from '@/assets/icons/aena/aena_documentacion.svg'
import { ReactComponent as AenaVacunacionIcon } from '@/assets/icons/aena/aena_vacunacion.svg'
import { ReactComponent as CatIcon } from '@/assets/icons/aena/aena_cat.svg'
import { ReactComponent as DogIcon } from '@/assets/icons/aena/aena_dog.svg'
import { BodyText, Headline2 } from '@/aena/styles'
import { useNavigate } from 'react-router-dom'
import { getHomeInfo, checkUserStatus } from '@front/apis'
import { useAppSelector } from '@front/store'
import { setPet } from '@/aena/my-store'
import { t } from 'i18next' 


interface Pet {
  guid: string
  name: string
  avatar: string
  species?: string
}


export const AenaHome: FC = () => {
  const scrollViewRef = useRef<ScrollView>(null)
  const [scrollX, setScrollX] = useState(0)
  const [scrollViewWidth, setScrollViewWidth] = useState(0)
  const [scrollRequestedPet, setScrollRequestedPet] = useState<string | null>(null)
  const navigate = useNavigate()
  const [homeData, setHomeData] = useState({} as any)
  const [userData, setUserData] = useState({} as any)
  const petGUID = useAppSelector((state) => state.selectedPet.guid) ?? ''
  const [selectedPet, setSelectedPet] = useState<string>(petGUID) 
  const petRefs = useRef<Record<string, View | null>>({})

  // Servicios visibles en la home. Para habilitar/deshabilitar uno basta con
  // cambiar su `enabled`; los deshabilitados no se pintan.
  const services = ([
    { id: 'qr', enabled: true, label: t('common.digitalpetpass').toUpperCase(), icon: require('@/assets/icons/pass.png'), url: `passport` },
    { id: 'sos', enabled: false, label: t('common.petpasssos').toUpperCase(), iconSvg: <LostPinIcon width={32} height={40} />, url: `sos` },
    { id: 'chat', enabled: false, label: t('common.petpassvetchat').toUpperCase(), icon: require('@/assets/icons/cruz.png'), url: `vetchat` },
    { id: 'viajes', enabled: true, label: t('common.travelcheck-in').toUpperCase(), icon: require('@/assets/icons/travel.png'), url: `travel` },
    { id: 'travel-docs', enabled: false, label: 'DOCUMENTACIÓN VIAJES EXTERIOR', iconSvg: <AenaDocumentacionIcon width={32} height={40} />, url: `travel-docs` },
  ] as Array<{ id: string; enabled: boolean; label: string; icon?: any; iconSvg?: React.ReactNode; url: string }>)
    .filter((service) => service.enabled)
  
  const handleSelectPet = (petGUID: string) => {
    console.log('SELECTING PET HOME', petGUID)
    setSelectedPet(petGUID)
    return setPet(petGUID)
  }

  useEffect(() => {
    const fetchHomeInfo = async () => {
      const home = await getHomeInfo({ lat: 0, lon: 0 })
      console.log('HOME DATA', home.data)
      setHomeData(home.data)
      const petToSelect =  selectedPet || (home.data?.petList?.[0]?.guid ?? "")
      handleSelectPet(petToSelect)
      setScrollRequestedPet(petToSelect);
    }

    fetchHomeInfo()

    const resUserStatus = async () => {
      const userstatus = await checkUserStatus({ lat: 0, lon: 0 })
      setUserData(userstatus.data)
    }
    resUserStatus()
  }, [])

  useEffect(() => {
    if (!scrollRequestedPet || !scrollViewRef.current || !petRefs.current[scrollRequestedPet]) return

    requestAnimationFrame(() => {
      petRefs.current[scrollRequestedPet]?.measureLayout(
        findNodeHandle(scrollViewRef.current) as number,
        (x, _y, width) => {
          const visibleStart = scrollX
          const visibleEnd = scrollX + scrollViewWidth

          const elementStart = x
          const elementEnd = x + width

          const isOutOfView = elementStart < visibleStart || elementEnd > visibleEnd

          if (isOutOfView) {
            const scrollToX = elementStart - scrollViewWidth / 2 + width / 2
            scrollViewRef.current?.scrollTo({ x: scrollToX, animated: true })
          }

          // Una vez hecho el scroll (o no), borramos la intención
          setScrollRequestedPet(null)
        },
        () => {
          console.warn('No se pudo medir el layout')
          setScrollRequestedPet(null) // Previene loops infinitos
        }
      )
    })
  }, [scrollRequestedPet, scrollX, scrollViewWidth])

  if (!homeData) return <View><BodyText>{t('common.loading')}</BodyText></View>

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>
        <View style={{ padding: 16 }}>
          <Headline2 style={{ marginBottom: 12, marginTop: 20 }}>{t('common.my_pets')}</Headline2>

          <ScrollView
            ref={scrollViewRef}
            horizontal
            onScroll={(event) => setScrollX(event.nativeEvent.contentOffset.x)}
            onLayout={(event) => setScrollViewWidth(event.nativeEvent.layout.width)}
            scrollEventThrottle={16}

            showsHorizontalScrollIndicator={Platform.OS === 'web'}
            contentContainerStyle={{
              paddingBottom: 16,
              display: 'flex',
            }}
            style={{
              overflowX: Platform.OS === 'web' ? 'auto' : undefined,
              display: Platform.OS === 'web' ? 'flex' : undefined,
            } as any}
          >
            {homeData?.petList?.map((pet: Pet & { guid: string }) => (
              <TouchableOpacity
                key={pet.guid}
                onPress={() => handleSelectPet(pet.guid)}
                style={{
                  marginRight: 16,
                  alignItems: 'center',
                }}
              >
                <View
                  ref={(ref) => {
                    if (ref) petRefs.current[pet.guid] = ref
                  }}
                  style={{
                    padding: 3, // crea espacio para el borde blanco
                    borderRadius: 53, // ligeramente mayor que la imagen
                    backgroundColor: '#ffffff', // borde blanco alrededor
                    borderWidth: 2,
                    borderColor: pet.guid === selectedPet ? '#96ce00' : '#fff', // borde verde si está seleccionado
                  }}
                >
                  {pet.avatar ? (
                    <Image
                      source={{ uri: pet.avatar }}
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: 50,
                      }}
                    />
                  ) : (
                    <View
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: 50,
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f6f6f6',
                      }}
                    >
                      {pet.species === 'CAT' ? <CatIcon width={56} height={56} /> : <DogIcon width={56} height={56} />}
                    </View>
                  )}
                </View>
                <BodyText>{pet.name}</BodyText>
              </TouchableOpacity>
            ))}
            {/* Botón de añadir mascota */}
            <TouchableOpacity
              onPress={() => navigate('/aena/pet/add')}
              style={{
                  marginRight: 16,
                  alignItems: 'center',
                }}
            >
                <View
                  style={{
                    padding: 3, // crea espacio para el borde blanco
                    borderRadius: 53, // ligeramente mayor que la imagen
                    backgroundColor: '#ffffff', // borde blanco alrededor
                    borderWidth: 2,
                    borderColor: '#fff', // borde verde si está seleccionado
                  }}
                >
                  <AddIcon width={100} height={100} />
                </View>              
                <BodyText>{t('common.addpet')}</BodyText>

            </TouchableOpacity>
          </ScrollView>

          {/* <Headline2 style={{ marginBottom: 4 }}>{t('common.petpassplans')}</Headline2>
          <View style={{ marginBottom: 18, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
            <DigitalPetPassIcon width={300} height={127} />
          </View> */}

          <Headline2 style={{ marginBottom: 12, marginTop: 20 }}>{t('common.petpassservices')}</Headline2>

          <View>
            {services.map((service) => (
              <TouchableOpacity
                key={service.id}
                onPress={() => {
                  if (selectedPet) navigate(`/aena/${service.url}`)
                }}
                activeOpacity={selectedPet ? 1 : 0.7}
                style={{
                  backgroundColor: !selectedPet ? '#ccc' : '#96ce00',
                  borderRadius: 8,
                  padding: 16,
                  marginBottom: 18,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  shadowColor: '#000',
                  shadowOpacity: 0.05,
                  shadowRadius: 6,
                  elevation: 2,
                }}
              >
                <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                  {service.iconSvg ? (
                    <View style={{ width: 35, height: 35, alignItems: 'center', justifyContent: 'center' }}>
                      {service.iconSvg}
                    </View>
                  ) : (
                    <Image source={service.icon} style={{ width: 25, height: 35 }} resizeMode="contain" />
                  )}
                  <BodyText style={{ fontWeight: 'bold' }}>{service.label}</BodyText>
                </View>
                <Text style={{ fontSize: 18 }}>›</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </AppBackground>
  )
}
